import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7b3b23d5"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "details-container" }
const _hoisted_3 = { class: "ratings-container" }
const _hoisted_4 = { class: "feedback-date-container" }

import { computed, inject, ref, watch } from 'vue';
import ModalBase from '@/components/ui/modals/modal-base.vue';
import { DxToolbarItem } from 'devextreme-vue/popup';
import { AcquaintApiClient } from '@/services/AcquaintApiClient';
import displayErrorMessage from '@/utils/ErrorHandling';
import { getErrorMessage } from 'bright-logic-core/src/errors';
import StarRatingInput from '../inputs/star-rating-input.vue';
import PropertyLookup from '@/components/ui/lookup/property-lookup.vue';
import ContactLookup from '@/components/ui/lookup/contact-lookup.vue';
import AcquaintTextArea from '@/components/ui/inputs/acquaint-text-area.vue';
import { TimeStamps } from '@/utils/TimeStamp';
import { PropertyDetailsSent } from 'acquaint-api/models/PropertyDetailsSent';
import SystemCodeSingleSelect from '@/components/ui/system-codes/system-code-single-select.vue';
import { CodeType } from 'acquaint-api/constants/CodeType';
import { PropertyDetailsSentValidation } from 'acquaint-api/validation/PropertyDetailsSentValidation';
import EmailFormModal from '@/components/email/email-form-modal.vue';
import { EmailTemplates } from '@/enums/contacts/EmailTemplates';
import { PropertySummary } from '@/types/properties/PropertySummary';
import { Owner } from '@/types/properties/Owner';
import moment from 'moment';
import AcquaintInfoBox from '../inputs/acquaint-info-box.vue';
import { formatDate } from 'bright-logic-core/src/format';
import { FieldSize } from '@/enums/system/FieldSize';
import { displayQuestionAlert } from '@/alerts/questionAlert';
import { TextboxValueChangeEvent } from '@/enums/ui/inputs/TextboxValueChangeEvent';


export default /*#__PURE__*/_defineComponent({
  __name: 'edit-feedback-modal',
  props: {
    /** 
     * The boolean value used with `v-model` to control the visibility or state of a modal or component.
     * @type {boolean}
     * @required
     */
    modelValue: {
        type: Boolean,
        required: true
    },
    /** 
     * The unique identifier for the contact.
     * @type {number}
     * @default 0
     */
    contactId: {
        type: Number,
        default: 0
    },
    /** 
     * The unique identifier for the property.
     * @type {number}
     * @default 0
     */
    propertyId: {
        type: Number,
        default: 0
    }
},
  emits: [
    /** 
     * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
     * @property {boolean} value - Indicates whether the modal has been opened or closed.
     */
    'update:modelValue',
    /** 
     * Triggered when the modal is closed.
     * @property {boolean} value - Indicates whether the changes have been saved.
     */
    'modalClosed',
    /** 
    * Triggered so that the appointment is locked when the user is navigated away to the contact's record page.
    */
    'navigateToContact',
    /** 
    * Triggered so that the appointment is locked when the user is navigated away to the property's record page.
    */
    'navigateToProperty'
],
  setup(__props, { emit }) {

const props = __props;





const apiClient = inject('AcquaintApiClient') as AcquaintApiClient;
const displayCancelConfirmationModal = ref(false);
const keepingModalOpening = ref(false);
const saving = ref(false);
const originalAppointmentFeedback = ref<PropertyDetailsSent>();
const appointmentFeedback = ref<PropertyDetailsSent>();

const showModal = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value),
});

const onCancelClick = () => showModal.value = false;

watch(() => showModal.value, async () => {
    if (!showModal.value || keepingModalOpening.value) {
        return;
    }

    originalAppointmentFeedback.value = undefined;
    appointmentFeedback.value = undefined;

    if (await fetchData()) {
        appointmentFeedback.value = JSON.parse(JSON.stringify(originalAppointmentFeedback.value));
    }

    await getPropertyOwner();
});

const fetchData = async () => {
    try {
        originalAppointmentFeedback.value = await apiClient.httpGet(`database/propertydetailssent/${props.contactId}/${props.propertyId}`);
        return true;
    } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
    }
};

watch(() => appointmentFeedback.value, () => {
    displayCancelConfirmationModal.value = JSON.stringify(originalAppointmentFeedback.value) !=
        JSON.stringify(appointmentFeedback.value);
}, { deep: true });

const modalClosed = async () => {
    keepingModalOpening.value = false;
    displayCancelConfirmationModal.value = false;
    emit('modalClosed', false);
};

const cancelPromptDenied = () => {
    keepingModalOpening.value = true;
};

const submitData = async () => {
    try {
        apiClient.httpPut(`database/propertydetailssent/${props.contactId}/${props.propertyId}`, appointmentFeedback.value)
        return true;
    } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
    }
};

const onSaveClick = async () => {
    saving.value = true;

    /* To match Desktop Acquaint, when details are changed the added date should be updated
     * This allows the feedback date to be acurately displayed in reports */
    if (displayCancelConfirmationModal.value && appointmentFeedback.value) {
        appointmentFeedback.value.addedDate = moment().format('YYYY-MM-DDTH:mm');
    }

    if (await submitData()) {
        displayCancelConfirmationModal.value = false;
        emit('modalClosed', true);
        showModal.value = false;
    }
    saving.value = false;
};

const onDateStampClick = async () => {
    if (appointmentFeedback.value) {
        appointmentFeedback.value.notes = await TimeStamps(appointmentFeedback.value.notes);
    }
};

const onShareFeedbackClick = async () => {
    // To avoid spamming the owner with the same feedback, we should ask for confirmation before re-sending feedback
    if (displayShareFeedbackConfirmation()) {
        const result = await displayQuestionAlert(`This feedback was previously sent to the owner on ${feedbackSentDate.value}. Are you sure you want to resend it?`)

        if (!result.isConfirmed) {
            return;
        }
    }

    await onSaveClick();
    showEmailForm.value = true;
}

const displayShareFeedbackConfirmation = () => {
    // To improve UX, don't ask user to confirm share feedback if there are new changes or if no feedback has been sent previously
    if (displayCancelConfirmationModal.value || !appointmentFeedback.value?.ownerFeedbackSentDate) {
        return false;
    }

    /* Only need to display confirmation prompt when the feedback has already been sent
     * This needs to compare the sent date with the added date, as the added date gets updated each time the feedback is updated*/
    return moment(appointmentFeedback.value.ownerFeedbackSentDate).isAfter(moment(appointmentFeedback.value.addedDate));
}

const getPropertyOwner = async () => {
    let property: PropertySummary | undefined = undefined;

    try {
        property = await apiClient.httpGet<PropertySummary>(`app/properties/${props.propertyId}/summary`)
    } catch {
        displayErrorMessage('Unable to read property details.');
        return;
    }

    if (!property) {
        propertyOwner.value = undefined;
        return;
    }

    propertyOwner.value = property.owner;
}

const showEmailForm = ref(false);
const propertyOwner = ref<Owner>();

const emailContacts = computed(() => propertyOwner.value ? [propertyOwner.value.id] : []);
const shareFeedbackDisabled = computed(() => saving.value || !propertyOwner.value?.emailAddress);

const feedbackSentDate = computed(() => appointmentFeedback.value?.ownerFeedbackSentDate ? formatDate(appointmentFeedback.value.ownerFeedbackSentDate, true) : 'Not Sent');


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showModal).value = $event)),
      title: "Appointment Feedback",
      onCancelPromptDenied: cancelPromptDenied,
      height: "595px",
      onModalClosed: modalClosed,
      showCloseConfirmationPrompt: displayCancelConfirmationModal.value,
      "help-page": "feedback",
      saving: saving.value
    }, {
      content: _withCtx(() => [
        (appointmentFeedback.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(ContactLookup, {
                modelValue: appointmentFeedback.value.contactID,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((appointmentFeedback.value.contactID) = $event)),
                label: "Contact",
                readOnly: true,
                onNavigateToContact: _cache[1] || (_cache[1] = ($event: any) => (emit('navigateToContact', appointmentFeedback.value.contactID)))
              }, null, 8, ["modelValue"]),
              _createVNode(PropertyLookup, {
                modelValue: appointmentFeedback.value.propertyID,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((appointmentFeedback.value.propertyID) = $event)),
                label: "Property",
                readOnly: true,
                onNavigateToProperty: _cache[3] || (_cache[3] = ($event: any) => (emit('navigateToProperty', appointmentFeedback.value.propertyID)))
              }, null, 8, ["modelValue"]),
              _createVNode(SystemCodeSingleSelect, {
                modelValue: appointmentFeedback.value.response,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((appointmentFeedback.value.response) = $event)),
                "code-type": _unref(CodeType).PropertyDetailsSentResponse,
                label: "Response",
                width: _unref(FieldSize).SmallMedium
              }, null, 8, ["modelValue", "code-type", "width"]),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(StarRatingInput, {
                    label: "Location Rating",
                    modelValue: appointmentFeedback.value.locationRating,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((appointmentFeedback.value.locationRating) = $event))
                  }, null, 8, ["modelValue"]),
                  _createVNode(StarRatingInput, {
                    label: "Condition Rating",
                    modelValue: appointmentFeedback.value.conditionRating,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((appointmentFeedback.value.conditionRating) = $event))
                  }, null, 8, ["modelValue"]),
                  _createVNode(StarRatingInput, {
                    label: "Price Rating",
                    modelValue: appointmentFeedback.value.priceRating,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((appointmentFeedback.value.priceRating) = $event))
                  }, null, 8, ["modelValue"])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(AcquaintInfoBox, {
                    label: "Feedback Sent On",
                    text: feedbackSentDate.value,
                    width: _unref(FieldSize).Small
                  }, null, 8, ["text", "width"])
                ])
              ]),
              _createVNode(AcquaintTextArea, {
                label: "Feedback Notes",
                modelValue: appointmentFeedback.value.notes,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((appointmentFeedback.value.notes) = $event)),
                "field-validation": _unref(PropertyDetailsSentValidation).Notes,
                "value-change-event": _unref(TextboxValueChangeEvent).KeyUp
              }, null, 8, ["modelValue", "field-validation", "value-change-event"])
            ]))
          : _createCommentVNode("", true)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Date Stamp', onClick: () => { onDateStampClick() }, disabled: saving.value }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Send to Owner', onClick: () => { onShareFeedbackClick() }, disabled: shareFeedbackDisabled.value }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Save', onClick: () => { onSaveClick() }, disabled: saving.value }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Cancel', onClick: () => { onCancelClick() }, disabled: saving.value }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "showCloseConfirmationPrompt", "saving"]),
    _createVNode(EmailFormModal, {
      modelValue: showEmailForm.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showEmailForm).value = $event)),
      "email-template-id": _unref(EmailTemplates).PropertyFeedback,
      contactList: emailContacts.value,
      "property-feedback-contact-id": __props.contactId,
      "property-related-to-id": __props.propertyId
    }, null, 8, ["modelValue", "email-template-id", "contactList", "property-feedback-contact-id", "property-related-to-id"])
  ], 64))
}
}

})